import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { playChime } from '../lib/chime';

/**
 * CorporateAd — a 13s, 9:16 vertical corporate advertisement for J.HOUSE OF SECRETARY.
 * Built in-app from approved photoreal stills of the real team + navy/gold title cards,
 * slow Ken-Burns motion, elegant gold typography and a subtle ambient soundtrack.
 * Message: REAL PROBLEMS. REAL PEOPLE. REAL SUPPORT.
 */

const BRAND_LOGO = 'https://custom-images.strikinglycdn.com/res/hrscywv4p/image/upload/c_limit,fl_lossy,h_300,w_300,f_auto,q_auto/20749311/510656_82382.png';

const B = 'https://static.prod-images.emergentagent.com/jobs/17489ab5-de15-4f84-9cff-9210034deb04/images/';
const IMG = {
  p1: B + 'db0f62f3d16058bc5600dd668d6c96c7c685e0c2b0bca812a57f106a0b604f8e.jpeg',
  p2: B + 'd33717faa8a4fb2d054cdaa035aa293e5b9ec81f40cbda5e2019b57c50cd4bfc.jpeg',
  p3: B + 'db940e89921d65e0ed75a33ec393c77861811060da4232752105a6a7f08f3d85.jpeg',
  p4: B + '8991f0f588b8f84a7b6b8e3afd348bbe875308d33238a8ba63a856c4af036367.jpeg',
  hand: B + 'e5cd93bf97a685c312a2cbf7933410cedc9b2a85ff72eb4e02624d95cb64daf5.jpeg',
};
const VID = { closing: '/ad/closing.mp4' };

// Elegant STILLS version (real people photos + Ken-Burns). Order: red (p3) third, blonde (p4) last.
const SCENES = [
  { img: IMG.p1, dur: 3000, texts: ['JUST ONE CALL'], kb: 'in' },
  { img: IMG.p2, dur: 3500, texts: ['WE LISTEN.', 'WE ASSIST.', 'WE CONNECT.'], kb: 'left' },
  { img: IMG.p3, dur: 2800, texts: [], kb: 'right' },
  { img: IMG.hand, dur: 2200, texts: ['A REAL PERSON ANSWERS'], kb: 'in' },
  { img: IMG.p4, dur: 3000, texts: ["WHEN HELP CAN'T WAIT."], kb: 'in' },
];
const TIMED_MS = SCENES.reduce((a, s) => a + s.dur, 0); // 11000
const TOTAL_MS = TIMED_MS + 2000; // + final card

const kbVariants = {
  in: { initial: { scale: 1.02 }, animate: { scale: 1.16 } },
  left: { initial: { scale: 1.12, x: 20 }, animate: { scale: 1.16, x: -20 } },
  right: { initial: { scale: 1.12, x: -20 }, animate: { scale: 1.16, x: 20 } },
};

const makeAmbient = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const ctx = new AC();
    const master = ctx.createGain();
    master.gain.value = 0.0001;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass'; lp.frequency.value = 820; lp.Q.value = 0.6;
    lp.connect(master); master.connect(ctx.destination);
    [220, 277.18, 329.63].forEach((f, i) => {
      const osc = ctx.createOscillator(); const g = ctx.createGain();
      osc.type = i === 2 ? 'triangle' : 'sine'; osc.frequency.value = f;
      g.gain.value = i === 0 ? 0.5 : 0.3; osc.connect(g); g.connect(lp); osc.start();
    });
    const lfo = ctx.createOscillator(); const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.08; lfoGain.gain.value = 200; lfo.connect(lfoGain); lfoGain.connect(lp.frequency); lfo.start();
    const now = ctx.currentTime;
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.06, now + 2);
    return {
      stop: () => {
        try {
          const t = ctx.currentTime;
          master.gain.cancelScheduledValues(t);
          master.gain.setValueAtTime(master.gain.value, t);
          master.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
          setTimeout(() => { try { ctx.close(); } catch (_e) {} }, 1000);
        } catch (_e) { try { ctx.close(); } catch (_e2) {} }
      },
    };
  } catch (_e) { return null; }
};

const GoldPhone = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor"><path d="M6.62 10.79a15 15 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21c1.21.49 2.53.74 3.88.74a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.35.26 2.67.74 3.88a1 1 0 01-.21 1.11z" /></svg>
);

const TitleCard = ({ final }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center"
    style={{ background: 'radial-gradient(130% 120% at 50% 40%, #12233f 0%, #0a1628 55%, #060f1f 100%)' }}>
    {final && (
      <motion.div className="mb-5" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        {['REAL PROBLEMS.', 'REAL PEOPLE.', 'REAL SUPPORT.'].map((t, i) => (
          <motion.div key={t} className="text-white font-bold leading-tight text-2xl md:text-3xl" style={{ fontFamily: 'Georgia, serif' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 + i * 0.4, duration: 0.5 }}>
            <span className="text-[#d4b661]">{t.split(' ')[0]}</span> {t.split(' ').slice(1).join(' ')}
          </motion.div>
        ))}
      </motion.div>
    )}
    <motion.div className="flex items-center gap-3 text-[#d4b661]"
      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: final ? 1.6 : 0.1, duration: 0.6 }}>
      <GoldPhone className="w-8 h-8 md:w-10 md:h-10" />
    </motion.div>
    <motion.img src={BRAND_LOGO} alt="J.HOUSE OF SECRETARY" className="h-12 md:h-16 w-auto mt-3"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: final ? 1.9 : 0.25, duration: 0.6 }} />
    <motion.p className="mt-2 text-white text-base md:text-xl font-bold tracking-wide" style={{ fontFamily: 'Georgia, serif' }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: final ? 2.1 : 0.4, duration: 0.6 }}>
      J.HOUSE OF SECRETARY
    </motion.p>
  </div>
);

const SceneText = ({ texts }) => (
  <div className="absolute inset-x-0 bottom-0 p-6 pb-10 flex flex-col items-center gap-1 text-center"
    style={{ background: 'linear-gradient(to top, rgba(6,15,31,0.85), rgba(6,15,31,0.0))' }}>
    {texts.map((t, i) => (
      <motion.div key={t} className="text-[#e9d9a6] font-bold uppercase tracking-[0.12em] text-xl md:text-2xl drop-shadow"
        style={{ fontFamily: 'Georgia, serif' }}
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.8, duration: 0.5 }}>
        {t}
      </motion.div>
    ))}
  </div>
);

export const CorporateAd = ({ testid = 'corporate-ad' }) => {
  const [state, setState] = useState('poster'); // poster | playing | ended
  const [scene, setScene] = useState(0);
  const [muted, setMuted] = useState(false);
  const timers = useRef([]);
  const ambient = useRef(null);

  const clearTimers = useCallback(() => { timers.current.forEach(clearTimeout); timers.current = []; }, []);
  const stopAmbient = useCallback(() => { if (ambient.current) { ambient.current.stop(); ambient.current = null; } }, []);

  const runSequence = useCallback(() => {
    clearTimers();
    setScene(0);
    let acc = 0;
    SCENES.forEach((s, i) => {
      acc += s.dur;
      timers.current.push(setTimeout(() => {
        if (i < SCENES.length - 1) { setScene(i + 1); }
        else { setState('ended'); if (!muted) playChime(); }
      }, acc));
    });
  }, [clearTimers, muted]);

  const start = (replay) => {
    stopAmbient();
    if (!muted) { ambient.current = makeAmbient(); playChime(); }
    setState('playing');
    runSequence();
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      if (next) stopAmbient();
      else if (state === 'playing') ambient.current = makeAmbient();
      return next;
    });
  };

  useEffect(() => () => { clearTimers(); stopAmbient(); }, [clearTimers, stopAmbient]);

  const cur = SCENES[scene] || SCENES[0];

  return (
    <div className="relative w-full mx-auto rounded-2xl overflow-hidden select-none bg-[#060f1f]"
      style={{ aspectRatio: '9 / 16', maxHeight: '78vh', border: '1px solid rgba(201,168,76,0.4)', boxShadow: '0 24px 60px -18px rgba(0,0,0,0.8)' }}
      data-testid={testid}>

      {/* POSTER */}
      {state === 'poster' && (
        <button type="button" onClick={() => start(false)} className="absolute inset-0 group" data-testid={`${testid}-play`}
          style={{ background: 'radial-gradient(130% 120% at 50% 40%, #12233f 0%, #0a1628 55%, #060f1f 100%)' }}>
          <img src={IMG.p1} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <img src={BRAND_LOGO} alt="J.HOUSE OF SECRETARY" className="h-14 md:h-20 w-auto" />
            <span className="relative flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full transition-transform duration-300 group-hover:scale-110"
              style={{ background: 'linear-gradient(180deg,#d4b661,#a17f2f)', boxShadow: '0 10px 30px -6px rgba(201,168,76,0.6)' }}>
              <span className="absolute inset-0 rounded-full border-2 border-[#d4b661]/60 animate-ping opacity-60" />
              <svg className="w-7 h-7 md:w-8 md:h-8 text-[#0a1628] ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            </span>
            <span className="text-[#e9d9a6] text-[11px] md:text-sm uppercase tracking-[0.2em] font-semibold">Watch our story</span>
            <span className="absolute bottom-3 right-4 text-[10px] text-[#d4b661]/60 font-mono">0:{String(Math.round(TOTAL_MS / 1000)).padStart(2, '0')}</span>
          </div>
        </button>
      )}

      {/* PLAYER */}
      {(state === 'playing' || state === 'ended') && (
        <>
          <div className="absolute top-0 left-0 right-0 h-1 bg-white/10 z-30">
            <div className="h-full bg-gradient-to-r from-[#d4b661]/70 to-[#d4b661]"
              style={{ width: state === 'ended' ? '100%' : undefined, animation: state === 'playing' ? `ad-prog ${TIMED_MS}ms linear forwards` : 'none' }} />
          </div>
          <button type="button" onClick={toggleMute} className="absolute top-3 right-3 z-30 w-9 h-9 rounded-full bg-black/40 border border-[#d4b661]/30 text-[#d4b661] flex items-center justify-center hover:bg-black/60 transition" data-testid={`${testid}-mute`} aria-label="Toggle sound">
            {muted
              ? <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6" /></svg>
              : <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5L6 9H2v6h4l5 4V5zM15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13" /></svg>}
          </button>

          <AnimatePresence>
            {state === 'playing' && (
              <motion.div key={`s${scene}`} className="absolute inset-0 overflow-hidden bg-black"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.45 }}>
                {cur.video ? (
                  <video src={cur.video} className="absolute inset-0 w-full h-full object-cover" autoPlay muted playsInline preload="auto" />
                ) : (
                  <motion.img src={cur.img} alt="" className="absolute inset-0 w-full h-full object-cover"
                    initial={kbVariants[cur.kb || 'in'].initial} animate={kbVariants[cur.kb || 'in'].animate}
                    transition={{ duration: cur.dur / 1000 + 0.6, ease: 'linear' }} />
                )}
                {cur.texts && cur.texts.length > 0 && <SceneText key={`t${scene}`} texts={cur.texts} />}
              </motion.div>
            )}
            {state === 'ended' && (
              <motion.div key="final" className="absolute inset-0 bg-black"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45 }}>
                <video src={VID.closing} className="absolute inset-0 w-full h-full object-cover" autoPlay muted playsInline preload="auto" />
                <motion.div className="absolute inset-x-0 top-[14%] flex flex-col items-center gap-1 text-center px-6"
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
                  {['REAL PROBLEMS.', 'REAL PEOPLE.', 'REAL SUPPORT.'].map((t, i) => (
                    <motion.span key={t} className="text-[#e9d9a6] font-bold uppercase tracking-[0.12em] text-lg md:text-xl drop-shadow" style={{ fontFamily: 'Georgia, serif' }}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 + i * 0.35, duration: 0.5 }}>{t}</motion.span>
                  ))}
                </motion.div>
                <div className="absolute inset-x-0 bottom-0 p-5 flex items-center justify-center gap-3 z-20">
                  <button type="button" onClick={() => start(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#d4b661]/50 text-[#d4b661] text-xs hover:bg-[#d4b661]/10 transition bg-black/30" data-testid={`${testid}-replay`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M5 9a7 7 0 0111.5-3.5M19 15a7 7 0 01-11.5 3.5" /></svg>
                    Replay
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}

      <style>{`@keyframes ad-prog { from { width: 0%; } to { width: 100%; } }`}</style>
    </div>
  );
};

export default CorporateAd;
